import { GroupPerson, GroupThing, Person, PersonType, Thing, ThingType } from '@eagle/core-data-types';
import { Chip, Stack, styled } from '@ethings/ui-toolkit';
import { Box, Grid } from '@mui/material';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useSmallScreen } from '../../hooks/use-small-screen';
import { FormatTimestamp } from '../format/format-timestamp';
import { ListPaperColumn } from '../list-item/list-paper-column';
import { AssignedBy } from './assigned-by';

interface EntityGroupRowProps<T, U, V> {
  actionMenu: JSX.Element;
  entity: T;
  entityGroup: V;
  entityType: U;
}

const StyledChip = styled(Chip)({
  cursor: 'pointer',
});

/**
  * FUNCTION: Display normal row for thing-group-thing or person-group-person
  */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function EntityGroupEntityRow<
  T extends Thing | Person,
  U extends ThingType | PersonType,
  V extends GroupThing | GroupPerson
>({ actionMenu, entity, entityType, entityGroup }: EntityGroupRowProps<T, U, V>): JSX.Element {
  const smallScreen = useSmallScreen();
  const { t } = useTranslation(['common']);

  return (
    <Stack direction="row" spacing={1} justifyContent={'space-between'} flexGrow={1} useFlexGap>
      <Grid container spacing={1}>
        <Grid item xs={6} sm={3}>
          <ListPaperColumn
            label={entityType.display}
            sx={{ justifyContent: 'stretch' }}
            width='100%'
          >
            {entity.display}
          </ListPaperColumn>
        </Grid>
        {!smallScreen
          && <>
            <Grid item sm={3}>
              <ListPaperColumn
                label={t('common:terms.start')}
                width='100%'
              >
                <Box
                  component='span'
                  sx={{ mr: 1 }}
                >
                  <FormatTimestamp value={entityGroup.start?.when} format="relative" />
                </Box>
                {entityGroup.startType === 'automatic' && <StyledChip label={t('common:terms.auto')} size="small" variant="outlined" />}
              </ListPaperColumn>
            </Grid>
            <AssignedBy label={t('common:common.started-by.labels')} who={entityGroup.start.who} />
          </>
        }
        <Grid item xs={6} sm={3} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          {actionMenu}
        </Grid>
      </Grid>
    </Stack>
  );
}
